import React, { useEffect, useState, useRef } from "react";
import Header from "../../../../Componets/Header";
import Loadercomponent from "../../../../Componets/Loader";
import toast from "react-hot-toast";
import { Button, Tag, Alert } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  PrinterOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { get_day_book_reports } from "../../../../Controllers/Dashboard/Dashboard";
import ScrollWrapper from "../../../../Componets/ScrollWrapper";
import { resolveTransactionType } from "../../../../Main/resolveTransactionType";
import { Tooltip } from "antd";
import Divider from "../../../../Componets/Divider";
import Table from "../../../../Componets/Table";
import UserCurrency from "../../../../Componets/UserCurrency";

const DayBookReport = () => {
  let componentRef = useRef();
  const [loader, setLoader] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [productSearchAlert, setProductSearchAlert] = useState(false);
  const [body, setBody] = useState([]);
  const PAGE_SIZE = 50;
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    pageSize: PAGE_SIZE,
  });

  const [summary, setSummary] = useState({
    totalSales: 0,
    totalPurchases: 0,
    totalSalesReturns: 0,
    totalPurchaseReturns: 0,
    totalCreditNotes: 0,
    totalDebitNotes: 0,
    totalExpenses: 0,
    totalTransfers: 0,
    totalAdjustments: 0,
    totalBookings: 0,
    totalInflow: 0,
    totalOutflow: 0,
  });

  const headers = {
    name: "Day Book Report",
    components: [
      {
        type: "range",
        name: "date",
        value: {
          start: dayjs().startOf("day").format("YYYY-MM-DD"),
          end: dayjs().endOf("day").format("YYYY-MM-DD"),
        },
      },
    ],
    navigate: {
      tabs: [
        {
          name: "General Ledger",
          path: "/app/common/reports",
        },
        {
          name: "Trial Balance",
          path: "/app/common/reports/trail-balance",
        },
        {
          name: "Balance Sheet",
          path: "/app/common/reports/balance-sheet",
        },
        {
          name: "Profit & Loss",
          path: "/app/common/reports/profit-loss",
        },
        {
          name: "AR Report",
          path: "/app/common/reports/ar-report",
        },
        {
          name: "AP Report",
          path: "/app/common/reports/ap-report",
        },
        {
          name: "Overall Transaction Report",
          path: "/app/common/reports/overall-transaction",
        },
        {
          name: "Day Book Report",
          path: "",
        },
      ],
      total: [
        { value: 0, label: "Total Transactions" },
        { value: 0, label: "Inflow" },
        { value: 0, label: "Outflow" },
      ],
    },
  };

  const [header, setHeader] = useState(headers);

  const tables = [
    { name: "Date & Time", key: "Date & Time" },
    { name: "Customer / Supplier Name", key: "Customer / Supplier Name" },
    { name: "Product", key: "Products" },
    { name: "Movement", key: "Movements" },
    { name: "Current Inventory stock", key: "Current Inventory Stock" },
    { name: "Debit", key: "Debit" },
    { name: "Credit", key: "Credit" },
    { name: "User", key: "User" },
    { name: "Details", key: "Details" },
  ];

  const computeQuantity = (type, quantity, isDelete = false, conversion) => {
    switch (type) {
      case "inventory":
        return isDelete ? -Math.abs(quantity) : Math.abs(quantity);
      case "purchase":
        return isDelete ? -Math.abs(quantity) : Math.abs(quantity);
      case "purchase_return":
        return isDelete ? Math.abs(quantity) : -Math.abs(quantity);
      case "debit_note":
        return isDelete ? Math.abs(quantity) : -Math.abs(quantity);
      case "credit_note":
        return isDelete ? Math.abs(quantity) : Math.abs(quantity);
      case "transfer":
        return isDelete ? Math.abs(quantity) : -Math.abs(quantity);
      case "receive":
        return isDelete ? Math.abs(quantity) : -Math.abs(quantity);
      case "received_current":
        return Math.abs(quantity);
      case "sale":
        return -Math.abs(quantity);
      case "sales_return":
        return conversion > 0
          ? Math.abs(quantity * conversion)
          : Math.abs(quantity);
      default:
        return quantity;
      case "booking":
        return -Math.abs(quantity);
      case "adjustment":
        return -Math.abs(quantity);
    }
  };

  const getSafeTimestamp = (entry) => {
    if (Number.isFinite(entry.timestamp)) {
      return entry.timestamp;
    }

    if (entry.date) {
      const t = new Date(entry.date).getTime();
      if (!Number.isNaN(t)) return t;
    }

    return null;
  };

  const getJournalAmounts = (journalEntry, journalAccounts = []) => {
    let debit = [];
    let credit = [];

    const accountMap = {};

    journalAccounts.forEach((account) => {
      accountMap[account._id] = account.name;
    });

    if (journalEntry?.entries?.length) {
      journalEntry.entries.forEach((entry) => {
        const accountName = accountMap[entry.account] || "Unknown Account";

        const value = `${accountName} (${Number(entry.amount || 0).toFixed(2)})`;

        if (entry.type === "debit") {
          debit.push(value);
        } else if (entry.type === "credit") {
          credit.push(value);
        }
      });
    }

    return {
      debit: debit.join(", "),
      credit: credit.join(", "),
    };
  };

  const processStockData = (apiData) => {
    const stockEntries = [];
    let productList = new Set();

    let summaryData = {
      totalSales: 0,
      totalPurchases: 0,
      totalSalesReturns: 0,
      totalPurchaseReturns: 0,
      totalCreditNotes: 0,
      totalDebitNotes: 0,
      totalExpenses: 0,
      totalTransfers: 0,
      totalAdjustments: 0,
      totalBookings: 0,
      totalInflow: 0,
      totalOutflow: 0,
    };

    const addEntry = (
      type,
      item,
      quantity,
      stockAfter,
      units,
      foc,
      date,
      info,
      details = {},
      debitAmount = 0,
      creditAmount = 0,
    ) => {
      const entryDate = new Date(item.created || item.createdAt || item.date);
      const formattedDate = dayjs(entryDate).format("YYYY-MM-DD HH:mm:ss");

      const { action, entity } = resolveTransactionType(info?.endpoint);

      let transactionLabel = "";
      let transactionType = type;

      switch (type) {
        case "received":
          transactionLabel = "STOCK RECEIVED (FROM OTHER BRANCH)";
          break;
        case "received_current":
          transactionLabel = "STOCK RECEIVED (CURRENT BRANCH)";
          break;
        case "debit_note":
          transactionLabel = "DEBIT NOTE (PURCHASE RETURN)";
          break;
        case "credit_note":
          transactionLabel = "CREDIT NOTE (SALES RETURN)";
          break;
        default:
          transactionLabel =
            action !== "UNKNOWN"
              ? `${action.replace("_", " ")} ${entity.replace("_", " ")}`
              : type.replace("_", " ").toUpperCase();
      }

      const amount = details?.amount || 0;
      const qty = Math.abs(quantity);

      switch (type) {
        case "sale":
          summaryData.totalSales += amount || 0;
          summaryData.totalOutflow += qty;
          break;
        case "purchase":
          summaryData.totalPurchases += amount || 0;
          summaryData.totalInflow += qty;
          break;
        case "sales_return":
          summaryData.totalSalesReturns += amount || 0;
          summaryData.totalInflow += qty;
          break;
        case "purchase_return":
          summaryData.totalPurchaseReturns += amount || 0;
          summaryData.totalOutflow += qty;
          break;
        case "credit_note":
          summaryData.totalCreditNotes += amount || 0;
          summaryData.totalInflow += qty;
          break;
        case "debit_note":
          summaryData.totalDebitNotes += amount || 0;
          summaryData.totalOutflow += qty;
          break;
        case "expense":
          summaryData.totalExpenses += amount || 0;
          break;
        case "transfer":
          summaryData.totalTransfers += 1;
          summaryData.totalOutflow += qty;
          break;
        case "adjustment":
          summaryData.totalAdjustments += 1;
          summaryData.totalOutflow += qty;
          break;
        case "booking":
          summaryData.totalBookings += amount || 0;
          summaryData.totalOutflow += qty;
          break;
      }

      let customerName = null;
      let supplierName = null;

      if (details?.customer) {
        customerName = typeof details.customer === 'string' ? details.customer : details.customer.name;
      } else if (item.customer?.name) {
        customerName = item.customer.name;
      } else if (info?.customer?.name) {
        customerName = info.customer.name;
      }

      if (details?.supplier) {
        supplierName = typeof details.supplier === 'string' ? details.supplier : details.supplier.name;
      } else if (item.supplier?.name) {
        supplierName = item.supplier.name;
      } else if (info?.supplier?.name) {
        supplierName = info.supplier.name;
      }

      let productName = "N/A";
      if (item.name) productName = item.name;
      else if (item.product?.name) productName = item.product.name;
      else if (info?.name) productName = info.name;
      else if (details?.productName) productName = details.productName;
      else if (info?.product?.name) productName = info.product.name;

      let userName = "System";
      if (item.user?.name) userName = item.user.name;
      else if (item.userId?.name) userName = item.userId.name;
      else if (details?.userName) userName = details.userName;
      else if (info?.user?.name) userName = info.user.name;

      stockEntries.push({
        key: `${type}_${item._id}_${entryDate.getTime()}`,
        date: formattedDate,
        timestamp: entryDate,
        type: transactionLabel,
        number: item.number,
        foc: foc,
        productName: productName,
        batchNumber: info?.number || info?.inventory?.number || "N/A",
        endpoint: info?.endpoint,
        stockAfter: stockAfter || 0,
        units: units || [],
        ismainunit: info?.description === info?.unit,
        expiryDate: info?.expiry_date
          ? dayjs(info.expiry_date).format("YYYY-MM-DD")
          : info?.inventory?.expiry_date
            ? dayjs(info.inventory.expiry_date).format("YYYY-MM-DD")
            : "N/A",
        productId: item._id || item.product?._id,
        reference:
          item.number || item.invoice || `#${type.slice(0, 3).toUpperCase()}`,
        quantity: Math.abs(quantity),
        movement: quantity > 0 ? "in" : "out",
        unit: "pcs",
        details: {
          ...details,
          customer: customerName,
          supplier: supplierName,
        },
        debit: debitAmount,
        credit: creditAmount,
        user: userName,
        branch: item.branch?.name || "Main Branch",
        customer: customerName,
        supplier: supplierName,
      });

      productList.add(productName);
    };

    if (apiData.data?.product_details) {
      apiData.data.product_details.forEach((product) => {
        const Units = product.unit_detail_log.map((unit) => ({
          _id: unit._id,
          name: unit.name,
          conversion: unit.conversion,
        }));
        addEntry(
          "product_created",
          product,
          0,
          product.current_stock,
          Units,
          0,
          product.created,
          product,
          {
            operation: "Product Created",
            price: product.sale_price,
            status: product.status === 1 ? "Active" : "Inactive",
          },
        );
      });
    }

    if (apiData.data?.inventory_details) {
      apiData.data.inventory_details.forEach((inv) => {
        const isDeleted =
          (inv.endpoint === "/api/delete-inventory") === true || false;
        const quantity = computeQuantity("inventory", inv.stock, isDeleted);
        const Units = inv.units.map((unit) => ({
          _id: unit._id,
          name: unit.name,
          conversion: unit.conversion,
        }));
        const isNegative = inv?.stock_change
          ? inv.stock_change.startsWith("-")
          : false;
        addEntry(
          "inventory",
          inv,
          inv.stock_change,
          isNegative ? 0 : inv.stock,
          Units,
          0,
          inv.createdAt,
          inv,
          {
            operation: inv?.note ? inv?.note : "Stock Addition",
            purchasePrice: inv.purchase_price,
            salePrice: inv.sale_price,
            expiry: inv.expiry_date
              ? dayjs(inv.expiry_date).format("YYYY-MM-DD")
              : null,
          },
        );
      });
    }

    if (apiData.data?.sales_details) {
      apiData.data.sales_details.forEach((sale) => {
        const { debit, credit } = getJournalAmounts(
          sale.journal_entry,
          sale.journal_accounts,
        );
        sale.details?.forEach((detail) => {
          const isDeleted =
            (sale.endpoint === "/api/delete-invoice") === true || false;
          const quantity = computeQuantity("sale", detail.quantity, isDeleted);
          const Units = detail.units.map((unit) => ({
            _id: unit._id,
            name: unit.name,
            conversion: unit.conversion,
          }));
          addEntry(
            "sale",
            sale,
            quantity,
            detail.inventory_stock_after,
            Units,
            detail?.free > 0 ? detail?.free : 0,
            sale.date,
            detail,
            {
              operation: "Sales Invoice",
              invoiceNumber: sale.number,
              customer: sale.customer?.name || "Walk-in Customer",
              amount: sale.total,
            },
            debit,
            credit,
          );
        });
      });
    }

    if (apiData.data?.sales_return_details) {
      apiData.data.sales_return_details.forEach((returnItem) => {
        const { debit, credit } = getJournalAmounts(
          returnItem.journal_entry,
          returnItem.journal_accounts,
        );
        returnItem.details?.forEach((detail) => {
          const isDeleted =
            (returnItem.endpoint === "/api/delete-sales-return") === true ||
            false;
          const quantity = computeQuantity(
            "sales_return",
            detail.quantity,
            isDeleted,
            detail.conversion,
          );
          const Units = [
            {
              _id: detail.unit,
              name: "Unknown Unit",
              conversion: detail.conversion,
            },
          ];
          addEntry(
            "sales_return",
            returnItem,
            quantity,
            detail.inventory_stock_after,
            Units,
            detail?.free > 0 ? detail?.free : 0,
            returnItem.date,
            detail,
            {
              operation: "Sales Return",
              returnNumber: returnItem.number,
              reason: "Customer Return",
              customer: returnItem.customer?.name || "Customer",
            },
            debit,
            credit,
          );
        });
      });
    }

    if (apiData.data?.purchase_order_details) {
      apiData.data.purchase_order_details.forEach((purchase) => {
        const { debit, credit } = getJournalAmounts(
          purchase.journal_entry,
          purchase.journal_accounts,
        );
        purchase.details?.forEach((detail) => {
          const isDeleted =
            (purchase.endpoint === "/api/delete-purchase-order") === true ||
            false;
          const quantity = computeQuantity(
            "purchase",
            detail.quantity,
            isDeleted,
          );
          const Units = detail.units.map((unit) => ({
            _id: unit._id,
            name: unit.name,
            conversion: unit.conversion,
          }));

          addEntry(
            "purchase",
            purchase,
            quantity,
            detail.inventory_stock_after,
            Units,
            detail?.free > 0 ? detail?.free : 0,
            purchase.date,
            detail,
            {
              operation: "Purchase Order",
              purchaseNumber: purchase.number,
              supplier: purchase.supplier?.name || "Supplier",
              amount: purchase.total,
            },
            debit,
            credit,
          );
        });
      });
    }

    if (apiData.data?.purchase_return_details) {
      apiData.data.purchase_return_details.forEach((returnItem) => {
        const { debit, credit } = getJournalAmounts(
          returnItem.journal_entry,
          returnItem.journal_accounts,
        );

        returnItem.details?.forEach((detail) => {
          const isDeleted =
            (returnItem.endpoint === "/api/delete-purchases-return") === true ||
            false;
          const quantity = computeQuantity(
            "purchase_return",
            detail.quantity,
            isDeleted,
          );
          const Units = [
            {
              _id: detail.unit,
              name: "Unknown Unit",
              conversion: detail.conversion,
            },
          ];
          addEntry(
            "purchase_return",
            returnItem,
            quantity,
            detail.inventory_stock_after,
            Units,
            detail?.free > 0 ? detail?.free : 0,
            returnItem.date,
            detail,
            {
              operation: "Purchase Return",
              returnNumber: returnItem.number,
              supplier: returnItem.supplier?.name || "Supplier",
            },
            debit,
            credit,
          );
        });
      });
    }

    if (apiData.data?.transfer_details) {
      apiData.data.transfer_details.forEach((transfer) => {
        const { debit, credit } = getJournalAmounts(
          transfer.journal_entry,
          transfer.journal_accounts,
        );
        transfer.details?.forEach((detail) => {
          const isDeleted =
            (transfer.endpoint === "/api/delete-transfer") === true || false;
          const quantity = computeQuantity(
            "transfer",
            detail.quantity,
            isDeleted,
          );
          const Units = detail.units.map((unit) => ({
            _id: unit._id,
            name: unit.name,
            conversion: unit.conversion,
          }));
          addEntry(
            "transfer",
            transfer,
            quantity,
            detail.inventory_stock_after,
            Units,
            0,
            transfer.date,
            detail,
            {
              operation: "Stock Transfer",
              transferNumber: transfer.number,
              toBranch: transfer.supplier_branch?.name || "Other Branch",
              status: transfer.delivery_status === 2 ? "Delivered" : "Pending",
            },
            debit,
            credit,
          );
        });
      });
    }

    if (apiData.data?.received_details) {
      apiData.data.received_details.forEach((received) => {
        const { debit, credit } = getJournalAmounts(
          received.journal_entry,
          received.journal_accounts,
        );
        received.details?.forEach((detail) => {
          const isDeleted =
            (received.endpoint === "/api/delete-transfer") === true || false;
          const quantity = computeQuantity(
            "received",
            detail.quantity,
            isDeleted,
          );
          const Units = detail.units
            ? detail.units.map((unit) => ({
                _id: unit._id,
                name: unit.name,
                conversion: unit.conversion,
              }))
            : [];
          addEntry(
            "received",
            received,
            quantity,
            detail.source_inventory_stock_after || detail.stock_after,
            Units,
            detail?.free || 0,
            received.date,
            detail,
            {
              operation:
                "Stock received by the branch to which it was transferred",
              receiveNumber: received.number,
              status: received.delivery_status === 2 ? "Delivered" : "Pending",
              sourceStockAfter: detail.source_inventory_stock_after,
              targetStockAfter: detail.target_inventory_stock_after,
            },
            debit,
            credit,
          );
        });
      });
    }

    if (apiData.data?.received_current_branch_details) {
      apiData.data.received_current_branch_details.forEach((received) => {
        const { debit, credit } = getJournalAmounts(
          received.journal_entry,
          received.journal_accounts,
        );
        received.details?.forEach((detail) => {
          const isDeleted =
            (received.endpoint === "/api/delete-transfer") === true || false;
          const quantity = computeQuantity(
            "received_current",
            detail.quantity,
            isDeleted,
          );
          const Units = detail.units
            ? detail.units.map((unit) => ({
                _id: unit._id,
                name: unit.name,
                conversion: unit.conversion,
              }))
            : [];
          addEntry(
            "received_current",
            received,
            quantity,
            detail.dest_inventory_stock_after,
            Units,
            detail?.free || 0,
            received.date,
            detail,
            {
              operation: "Transferred stock received by the current branch",
              receiveNumber: received.number,
              status:
                received.status || received.delivery_status === 2
                  ? "Delivered"
                  : "Pending",
            },
            debit,
            credit,
          );
        });
      });
    }

    if (apiData.data?.debit_note_details) {
      apiData.data.debit_note_details.forEach((debitNote) => {
        const { debit, credit } = getJournalAmounts(
          debitNote.journal_entry,
          debitNote.journal_accounts,
        );
        debitNote.details?.forEach((detail) => {
          const isDeleted =
            (debitNote.endpoint === "/api/delete-debit-note") === true || false;
          const quantity = computeQuantity(
            "debit_note",
            detail.quantity,
            isDeleted,
          );
          const Units = detail.units
            ? detail.units.map((unit) => ({
                _id: unit._id,
                name: unit.name,
                conversion: unit.conversion,
              }))
            : [];

          addEntry(
            "debit_note",
            debitNote,
            quantity,
            detail.inventory_stock_after,
            Units,
            detail?.free > 0 ? detail?.free : 0,
            debitNote.date,
            detail,
            {
              operation: "Debit Note (Purchase Return)",
              debitNoteNumber: debitNote.number,
              supplier: debitNote.supplier?.name || "Supplier",
              reason: debitNote.reason,
              amount: debitNote.total,
            },
            debit,
            credit,
          );
        });
      });
    }

    if (apiData.data?.credit_note_details) {
      apiData.data.credit_note_details.forEach((creditNote) => {
        const { debit, credit } = getJournalAmounts(
          creditNote.journal_entry,
          creditNote.journal_accounts,
        );
        creditNote.details?.forEach((detail) => {
          const isDeleted =
            (creditNote.endpoint === "/api/delete-credit-note") === true ||
            false;
          const quantity = computeQuantity(
            "credit_note",
            detail.quantity,
            isDeleted,
          );
          const Units = detail.units
            ? detail.units.map((unit) => ({
                _id: unit._id,
                name: unit.name,
                conversion: unit.conversion,
              }))
            : [];

          addEntry(
            "credit_note",
            creditNote,
            quantity,
            detail.inventory_stock_after,
            Units,
            detail?.free > 0 ? detail?.free : 0,
            creditNote.date,
            detail,
            {
              operation: "Credit Note (Sales Return)",
              creditNoteNumber: creditNote.number,
              customer: creditNote.customer?.name || "Customer",
              invoiceNumber: creditNote.invoice?.number || "N/A",
              reason: creditNote.reason,
              amount: creditNote.total,
            },
            debit,
            credit,
          );
        });
      });
    }

    if (apiData.data?.booking_details) {
      apiData.data.booking_details.forEach((booking) => {
        booking.details?.forEach((detail) => {
          const quantity = detail.quantity;

          addEntry(
            "booking",
            booking,
            -Math.abs(quantity),
            detail.inventory?.stock || 0,
            [],
            0,
            booking.date || booking.created,
            detail,
            {
              operation: "Booking Reserved",
              bookingNumber: booking.number,
              customer: booking.customer?.name || "Customer",
              amount: booking.total,
              booking_status: booking.booking_status,
              deposit: booking.total_deposited || 0,
            },
          );
        });
      });
    }

    if (apiData.data?.adjustment_details) {
      apiData.data.adjustment_details.forEach((adjustment) => {
        addEntry(
          "adjustment",
          adjustment,
          -Math.abs(adjustment.quantity),
          adjustment.stock_after,
          [],
          0,
          adjustment.date || adjustment.created,
          adjustment,
          {
            operation: `Stock Adjustment - ${adjustment.type}`,
            reason: adjustment.reason || "",
            note: adjustment.note || "",
            stockBefore: adjustment.stock_before,
            stockAfter: adjustment.stock_after,
          },
        );
      });
    }

    if (apiData.data?.expense_details) {
      apiData.data.expense_details.forEach((expense) => {
        const { debit, credit } = getJournalAmounts(
          expense.journal_entry,
          expense.journal_accounts,
        );

        addEntry(
          "expense",
          expense,
          0,
          "N/A",
          [],
          0,
          expense.date || expense.createdAt,
          expense,
          {
            operation: "Expense",
            category: expense.category?.name || "N/A",
            amount: expense.amount,
            payment: expense.payment,
            description: expense.description || "",
            number: expense.number,
            userName: expense.user?.name || "System",
          },
          debit || `-${expense.amount}`,
          credit || "0",
        );
      });
    }

    setSummary(summaryData);

    stockEntries.sort((a, b) => {
      const ta = getSafeTimestamp(a);
      const tb = getSafeTimestamp(b);

      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return tb - ta;
    });

    return stockEntries;
  };

  const fetchStockData = async (data) => {
    try {
      setLoader(true);
      const StockMovementAPIReponse = await get_day_book_reports(data);

      if (StockMovementAPIReponse?.data?.data) {
        const processedData = processStockData(StockMovementAPIReponse.data);

        setStockData(processedData);
        setFilteredData(processedData);

        const inflow = processedData.filter((d) => d.movement === "in").length;
        const outflow = processedData.filter(
          (d) => d.movement === "out",
        ).length;

        setHeader((prev) => ({
          ...prev,
          navigate: {
            ...prev.navigate,
            total: [
              { value: processedData.length, label: "Total Transactions" },
              { value: inflow, label: "Inflow" },
              { value: outflow, label: "Outflow" },
            ],
          },
        }));

        const totalPages = Math.ceil(processedData.length / PAGE_SIZE);

        setPagination({
          currentPage: 1,
          totalPages,
          totalCount: processedData.length,
          pageSize: PAGE_SIZE,
        });

        setProductSearchAlert(false);
      } else {
        setProductSearchAlert(true);
      }

      setLoader(false);
    } catch (error) {
      toast.error(error.message);
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchStockData();
  }, []);

  useEffect(() => {
    if (!filteredData) return;

    const start = (pagination.currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    const paginatedData = filteredData.slice(start, end);

    const tableBody = paginatedData.map((record, index) => {
      const d = new Date(record.date);

      const date =
        d.toLocaleDateString("en-GB") +
        " " +
        d.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

      const qty = record.quantity?.toFixed(0);
      const unit = record.ismainunit ? "F" : "L";
      const hasFoc = record.foc > 0;
      const details = record.details;

      return {
        "Date & Time": date,

        "Customer / Supplier Name": record.supplier
          ? record.supplier
          : record.customer
            ? record.customer
            : "-",

        Product: (
          <div className="text-xs">
            <div>
              <strong>Name:</strong> {record.productName}
            </div>
            <div>
              <strong>Batch:</strong> {record.batchNumber}
            </div>
            <div>
              <strong>Expiry:</strong> {record.expiryDate}
            </div>
          </div>
        ),

        Movement: (
          <div className="flex items-center">
            {record.movement === "in" ? (
              <>
                <ArrowUpOutlined className="text-green-600 mr-1" />
                <span className="text-green-600 flex items-center">
                  + {qty} {unit}
                  {hasFoc && (
                    <>
                      & {record.foc} FOC
                    </>
                  )}
                </span>
              </>
            ) : (
              <>
                <ArrowDownOutlined className="text-red-600 mr-1" />
                <span className="text-red-600 flex items-center">
                  - {qty} {unit}
                  {hasFoc && (
                    <>
                      & {record.foc} FOC
                    </>
                  )}
                </span>
              </>
            )}
          </div>
        ),

        "Current Inventory stock": renderStockAfter(record),

        Debit: (
          <span className="text-red-600 font-medium">
            {record.debit || "-"}
          </span>
        ),

        Credit: (
          <span className="text-green-600 font-medium">
            {record.credit || "-"}
          </span>
        ),

        User: record.user,

        Details: (
          <div className="text-xs text-left">
            <div>
              <strong>Op:</strong> {record.type}
            </div>
            {details.customer && (
              <div>
                <strong>Customer Name:</strong> {details.customer}
              </div>
            )}
            {details.supplier && (
              <div>
                <strong>Supplier Name:</strong> {details.supplier}
              </div>
            )}
            {details.receiveNumber && (
              <div>
                <strong>Rec. No:</strong> {details.receiveNumber}
              </div>
            )}
            {details.operation && (
              <div>
                <strong>Note:</strong> {details.operation}
              </div>
            )}
            <div
              dangerouslySetInnerHTML={{
                __html: formatOpLabel(
                  record.type,
                  record.number,
                  details.operation,
                ),
              }}
            />
          </div>
        ),
      };
    });

    setBody(tableBody);
  }, [filteredData, pagination.currentPage]);

  const renderStockAfter = (record) => {
    if (!record.units || record.units.length === 0) {
      return `${record.stockAfter} F`;
    }

    const fullQty = Math.floor(record.stockAfter);

    const looseUnits = record.units
      .map((unit) => {
        const looseQty = Math.floor(
          (record.stockAfter - fullQty) * unit.conversion + 1e-6,
        );
        return { ...unit, looseQty };
      })
      .filter((unit) => unit.looseQty > 0);

    return (
      <Tooltip
        placement="top"
        title={
          <div>
            <div>Full Quantity: {fullQty}</div>
            {looseUnits.map((unit) => (
              <div key={unit._id}>
                {unit.name}: {unit.looseQty}
              </div>
            ))}
          </div>
        }
      >
        <span className="flex items-center gap-2">
          <span>{fullQty} F</span>

          {looseUnits.length > 0 && (
            <>
              <Divider type="vertical" />
              {looseUnits.map((unit) => (
                <span key={unit._id}>{unit.looseQty} L</span>
              ))}
            </>
          )}
        </span>
      </Tooltip>
    );
  };

  const getStockAfterText = (record) => {
    if (!record.units || record.units.length === 0) {
      return `${record.stockAfter} F`;
    }

    const fullQty = Math.floor(record.stockAfter);

    const looseUnits = record.units
      .map((unit) => {
        const looseQty = Math.floor(
          (record.stockAfter - fullQty) * unit.conversion + 1e-6,
        );
        return { ...unit, looseQty };
      })
      .filter((unit) => unit.looseQty > 0);

    let text = `${fullQty} F`;
    if (looseUnits.length > 0) {
      text += " | ";
      text += looseUnits.map((unit) => `${unit.looseQty}L`).join(" | ");
    }

    return text;
  };

  const formatOpLabel = (op = "", number = "", note = "") => {
    if (!op) return null;

    const lowerOp = op.toLowerCase().trim();
    const lowerNote = note ? note.toLowerCase().trim() : "";

    const opPrefixMap = {
      inventory: "BATCH",
      transfer: "TR",
      receive: "RECEIVED",
      invoices: "INV",
      "sales return": "SR",
      purchase: "PO",
      adjustment: "ADJ",
      "purchase return": "PR",
      "debit note": "DN",
      "credit note": "CN",
      booking: "BK",
      expense: "EXP",
    };

    if (lowerNote && lowerNote.includes("inventory")) {
      return `<strong>Number:</strong> #BATCH-${number}`;
    }

    const prefix = Object.keys(opPrefixMap).find((key) =>
      lowerOp.includes(key),
    );

    if (!prefix) return `<strong>Number:</strong> ${number}`;

    const formattedNumber = `${opPrefixMap[prefix]}-${number}`;

    return `<strong>Number:</strong> #${formattedNumber}`;
  };

  const formatOpLabelForCSV = (op = "", number = "", note = "") => {
    if (!op) return null;

    const lowerOp = op.toLowerCase().trim();
    const lowerNote = note ? note.toLowerCase().trim() : "";

    const opPrefixMap = {
      inventory: "BATCH",
      transfer: "TR",
      receive: "RECEIVED",
      invoices: "INV",
      "sales return": "SR",
      purchase: "PO",
      "purchase return": "PR",
      "debit note": "DN",
      "credit note": "CN",
      booking: "BK",
      adjustment: "ADJ",
      expense: "EXP",
    };

    if (lowerNote && lowerNote.includes("inventory")) {
      return `Number: #BATCH-${number}`;
    }

    const prefix = Object.keys(opPrefixMap).find((key) =>
      lowerOp.includes(key),
    );

    if (!prefix) return `Number: ${number}`;

    const formattedNumber = `${opPrefixMap[prefix]}-${number}`;

    return `Number: #${formattedNumber}`;
  };

  const downloadPDF = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error("No data to export");
      return;
    }

    const doc = new jsPDF("landscape");

    const branchName = filteredData[0]?.branch || "N/A";

    doc.setFontSize(18);
    doc.text("Day Book Report", 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${dayjs().format("YYYY-MM-DD HH:mm:ss")}`, 14, 22);
    doc.text(`Branch: ${branchName}`, 14, 28);
    doc.text(`Total Records: ${filteredData.length}`, 14, 34);

    doc.setFontSize(9);
    let summaryY = 40;
    const summaryData = [
      [`Total Sales: ${summary.totalSales.toFixed(3)}`, `Total Purchases:  ${summary.totalPurchases.toFixed(3)}`],
      [`Sales Returns: ${summary.totalSalesReturns.toFixed(3)}`, `Purchase Returns:  ${summary.totalPurchaseReturns.toFixed(3)}`],
      [`Credit Notes: ${summary.totalCreditNotes.toFixed(3)}`, `Debit Notes:  ${summary.totalDebitNotes.toFixed(3)}`],
      [`Total Expenses: ${summary.totalExpenses.toFixed(3)}`, `Total Transfers: ${summary.totalTransfers}`],
      [`Total Bookings: ${summary.totalBookings.toFixed(3)}`, `Total Adjustments: ${summary.totalAdjustments}`],
    ];

    summaryData.forEach((row, index) => {
      doc.text(row[0], 14, summaryY + (index * 5));
      doc.text(row[1], 100, summaryY + (index * 5));
    });

    const tableData = filteredData.map((item, index) => {
      const d = new Date(item.date);

      const formattedDate =
        d.toLocaleDateString("en-GB") +
        " " +
        d.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

      const details = item.details || {};

      const movement =
        item.quantity > 0
          ? `+ ${item.quantity}${item.foc > 0 ? ` & ${item.foc} FOC` : ""}`
          : `- ${Math.abs(item.quantity)}${item.foc > 0 ? ` & ${item.foc} FOC` : ""}`;

      return [
        index + 1,
        formattedDate,
        item.supplier || item.customer || "-",
        `${item.productName}\nBatch : ${item.batchNumber}\nExpiry : ${item.expiryDate}`,
        movement,
        getStockAfterText(item),
        item.debit || "-",
        item.credit || "-",
        item.user || "-",
        [
          `Operation : ${item.type}`,
          item.customer ? `Customer : ${item.customer}` : "",
          item.supplier ? `Supplier : ${item.supplier}` : "",
          details.receiveNumber ? `Receive No : ${details.receiveNumber}` : "",
          formatOpLabelForCSV(item.type, item.number, details.operation),
        ]
          .filter(Boolean)
          .join("\n"),
      ];
    });

    autoTable(doc, {
      startY: 70,
      head: [
        [
          "S.No",
          "Date & Time",
          "Customer / Supplier Name",
          "Product",
          "Movement",
          "Current Stock",
          "Debit",
          "Credit",
          "User",
          "Details",
        ],
      ],
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 6,
        cellPadding: 1.5,
        overflow: "linebreak",
      },
      tableWidth: "auto",
    });

    doc.save(`day-book-full-${dayjs().format("YYYY-MM-DD-HHmm")}.pdf`);
    toast.success(`PDF exported with ${filteredData.length} records`);
  };

  const downloadCSV = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error("No data to export");
      return;
    }

    const summaryRows = [
      `"Day Book Report Summary"`,
      `"Date Generated",${dayjs().format("YYYY-MM-DD HH:mm:ss")}`,
      `"Total Transactions",${filteredData.length}`,
      `"Total Sales","${summary.totalSales.toFixed(3)}"`,
      `"Total Purchases","${summary.totalPurchases.toFixed(3)}"`,
      `"Sales Returns","${summary.totalSalesReturns.toFixed(3)}"`,
      `"Purchase Returns","${summary.totalPurchaseReturns.toFixed(3)}"`,
      `"Credit Notes","${summary.totalCreditNotes.toFixed(3)}"`,
      `"Debit Notes","${summary.totalDebitNotes.toFixed(3)}"`,
      `"Total Expenses","${summary.totalExpenses.toFixed(3)}"`,
      `"Total Transfers",${summary.totalTransfers}`,
      `"Total Adjustments",${summary.totalAdjustments}`,
      `"Total Bookings","${summary.totalBookings.toFixed(3)}"`,
      ``,
    ];

    const headers = [
      "S.No",
      "Date & Time",
      "Customer / Supplier Name",
      "Product",
      "Movement",
      "Current Inventory Stock",
      "Debit",
      "Credit",
      "User",
      "Details",
    ];

    const rows = filteredData.map((item, index) => {
      const d = new Date(item.date);

      const formattedDate =
        d.toLocaleDateString("en-GB") +
        " " +
        d.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

      const details = item.details || {};

      const movement =
        item.quantity > 0
          ? `+ ${item.quantity}${item.foc > 0 ? ` & ${item.foc} FOC` : ""}`
          : `- ${Math.abs(item.quantity)}${item.foc > 0 ? ` & ${item.foc} FOC` : ""}`;

      return [
        index + 1,
        formattedDate,
        item.supplier || item.customer || "-",
        `${item.productName} | Batch: ${item.batchNumber} | Exp: ${item.expiryDate}`,
        movement,
        getStockAfterText(item),
        item.debit || "-",
        item.credit || "-",
        item.user || "-",
        [
          `Operation: ${item.type}`,
          item.customer ? `Customer: ${item.customer}` : "",
          item.supplier ? `Supplier: ${item.supplier}` : "",
          details.receiveNumber ? `Receive No: ${details.receiveNumber}` : "",
          formatOpLabelForCSV(item.type, item.number, details.operation),
        ]
          .filter(Boolean)
          .join(" | "),
      ];
    });

    const csvContent = [
      ...summaryRows,
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = `day-book-full-${dayjs().format("YYYY-MM-DD-HHmm")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`CSV exported with ${filteredData.length} records`);
  };

  const formdata = (data) => {
    fetchStockData({
      ...data,
    });
  };

  const paginationdata = (data) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: data.currentPage,
    }));
  };

  return (
    <Header headers={header} setHeaders={setHeader} formdata={formdata}>
      <Loadercomponent loader={loader}>
        <div className="flex justify-between gap-2 mt-4">
          <div className="text-sm text-gray-800 flex flex-col gap-2">
          </div>
          <div className="flex gap-2">
            <Button icon={<DownloadOutlined />} onClick={downloadCSV}>
              CSV
            </Button>
            <Button icon={<PrinterOutlined />} onClick={downloadPDF}>
              PDF
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Total Sales</h3>
            <p className="text-lg font-semibold text-blue-600">
              <UserCurrency /> {summary.totalSales.toFixed(3)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Total Purchases</h3>
            <p className="text-lg font-semibold text-green-600">
               <UserCurrency /> {summary.totalPurchases.toFixed(3)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Sales Returns</h3>
            <p className="text-lg font-semibold text-orange-600">
               <UserCurrency /> {summary.totalSalesReturns.toFixed(3)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Purchase Returns</h3>
            <p className="text-lg font-semibold text-red-600">
               <UserCurrency /> {summary.totalPurchaseReturns.toFixed(3)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Credit Notes</h3>
            <p className="text-lg font-semibold text-purple-600">
               <UserCurrency /> {summary.totalCreditNotes.toFixed(3)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Debit Notes</h3>
            <p className="text-lg font-semibold text-indigo-600">
               <UserCurrency /> {summary.totalDebitNotes.toFixed(3)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Total Expenses</h3>
            <p className="text-lg font-semibold text-pink-600">
               <UserCurrency /> {summary.totalExpenses.toFixed(3)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Total Transfers</h3>
            <p className="text-lg font-semibold text-teal-600">
              {summary.totalTransfers}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Total Adjustments</h3>
            <p className="text-lg font-semibold text-amber-600">
              {summary.totalAdjustments}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-xs text-gray-500 mb-1">Total Bookings</h3>
            <p className="text-lg font-semibold text-cyan-600">
               <UserCurrency /> {summary.totalBookings.toFixed(3)}
            </p>
          </div>
        </div>

        <Table
          head={tables}
          body={body}
          pagination={pagination}
          paginationdata={paginationdata}
          componentRef={componentRef}
          summary={1}
        />
      </Loadercomponent>
    </Header>
  );
};

export default DayBookReport;